
import { supabase } from './supabase';
import { Order, OrderStatus, User, ServiceCategory, Urgency, Message, TimelineEvent, TopUpRequest, Transaction, MillingCenter, MillingServiceOffering, AppNotification, SystemSettings, DesignerServiceRate, ClinicInvite, ClinicBranch, PricePlan, PricePlanItem, UserRole } from '../types';

const mapProfileToUser = (profile: any): User => ({
    id: profile.id,
    name: profile.name,
    email: profile.email,
    role: profile.role,
    clinicName: profile.clinic_name,
    phone: profile.phone,
    secondaryPhones: profile.secondary_phones || [],
    location: profile.location,
    onboardingComplete: profile.onboarding_complete === true,
    balance: profile.balance || 0,
    isTrusted: profile.is_trusted || false,
    commissionFixed: profile.commission_fixed || 0,
    commissionPercentage: profile.commission_percentage || 0,
    isDefaultDesigner: profile.is_default_designer || false,
    parentId: profile.parent_id,
    canSeeFinancials: profile.can_see_financials,
    branchId: profile.branch_id,
    branchName: profile.clinic_branches?.name,
    pricePlanId: profile.price_plan_id
});

const mapOrderFromDB = (data: any): Order => ({
    id: data.id,
    patientName: data.patient_name,
    doctorName: data.doctor?.name || 'Unknown',
    doctorId: data.doctor_id,
    technicianId: data.technician_id,
    millingCenterId: data.milling_center_id,
    branchId: data.branch_id,
    branchName: data.clinic_branches?.name,
    toothNumber: data.tooth_number || '',
    shade: data.shade,
    status: data.status as OrderStatus,
    urgency: (data.urgency as Urgency) || Urgency.NORMAL,
    createdAt: data.created_at,
    dueDate: data.due_date,
    notes: data.notes,
    price: data.price,
    manualCost: data.manual_cost || 0,
    requiresDesignCheck: data.requires_design_check,
    isPMMA: data.is_pmma,
    designFiles: data.design_files || {},
    needsContact: data.needs_contact || false,
    isPaid: data.is_paid || false,

    items: (data.items || data.order_items)?.map((i: any) => ({
        serviceId: i.service_id,
        serviceName: i.service_name,
        teeth: i.teeth,
        price: i.price,
        details: i.details || ''
    })) || [],
    files: (data.files || data.order_files)?.map((f: any) => ({
        id: f.id,
        name: f.name,
        url: f.url,
        uploadedAt: f.uploaded_at,
        type: f.type,
        size: f.size
    })) || []
});

// Helper to gather all data needed for notifications safely
const getNotificationData = async (orderId: string) => {
    try {
        // 1. Fetch Order with Relations
        const { data: order, error } = await supabase.from('orders')
            .select(`
                *,
                doctor:profiles!orders_doctor_id_fkey(name, phone),
                technician:profiles!orders_technician_id_fkey(name, phone)
            `)
            .eq('id', orderId)
            .single();

        if (error || !order) return null;

        // 2. Get or Create Share Token (for links)
        let token = '';
        const { data: link } = await supabase.from('order_share_links').select('token').eq('order_id', orderId).limit(1).maybeSingle();
        if (link) {
            token = link.token;
        } else {
            // Create new if missing
            token = Math.random().toString(36).substring(2) + Math.random().toString(36).substring(2);
            await supabase.from('order_share_links').insert({ order_id: orderId, token });
        }

        return {
            order,
            doctor: order.doctor,
            technician: order.technician,
            token
        };
    } catch (e) {
        console.error("Error fetching notification data", e);
        return null;
    }
};

export const api = {
    // Auth & Session
    getSession: async () => {
        const { data } = await supabase.auth.getSession();
        return data.session;
    },

    logout: async () => {
        const { error } = await supabase.auth.signOut();
        if (error) throw error;
    },

    getCurrentUser: async (): Promise<User | null> => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return null;

        const { data: profile, error } = await supabase.from('profiles')
            .select('*, clinic_branches!profiles_branch_id_fkey(name)')
            .eq('id', user.id).single();

        if (error || !profile) return null;

        return mapProfileToUser(profile);
    },

    adminCreateUser: async (userData: any): Promise<User> => {
        const { data, error } = await supabase.functions.invoke('create-user', {
            body: userData
        });

        if (error) throw error;
        if (data.error) throw new Error(data.error);

        return data.user;
    },

    updateProfile: async (id: string, updates: any) => {
        const payload: any = {};
        if (updates.name) payload.name = updates.name;
        if (updates.clinicName) payload.clinic_name = updates.clinicName;
        if (updates.phone) payload.phone = updates.phone;
        if (updates.secondaryPhones) payload.secondary_phones = updates.secondaryPhones;
        if (updates.location) payload.location = updates.location;
        if (updates.onboardingComplete !== undefined) payload.onboarding_complete = updates.onboardingComplete;
        if (updates.isTrusted !== undefined) payload.is_trusted = updates.isTrusted;
        if (updates.role) payload.role = updates.role;
        if (updates.canSeeFinancials !== undefined) payload.can_see_financials = updates.canSeeFinancials;
        if (updates.branchId !== undefined) payload.branch_id = updates.branchId;
        if (updates.commissionFixed !== undefined) payload.commission_fixed = updates.commissionFixed;
        if (updates.commissionPercentage !== undefined) payload.commission_percentage = updates.commissionPercentage;
        if (updates.isDefaultDesigner !== undefined) payload.is_default_designer = updates.isDefaultDesigner;
        if (updates.pricePlanId !== undefined) payload.price_plan_id = updates.pricePlanId === 'default' ? null : updates.pricePlanId;

        const { error } = await supabase.from('profiles').update(payload).eq('id', id);
        if (error) throw error;
    },

    // Users Management
    getUsers: async (): Promise<User[]> => {
        const { data, error } = await supabase.from('profiles').select('*, clinic_branches!profiles_branch_id_fkey(name)');
        if (error) throw error;
        return data.map(mapProfileToUser);
    },

    deleteUser: async (id: string) => {
        const { error } = await supabase.functions.invoke('delete-user', {
            body: { userId: id }
        });
        if (error) throw error;
    },

    adjustUserBalance: async (userId: string, amount: number, description: string, type: string) => {
        const { error } = await supabase.rpc('adjust_balance', {
            p_user_id: userId,
            p_amount: amount,
            p_description: description,
            p_type: type
        });
        if (error) throw error;
    },

    // Clinic Management
    createBranches: async (doctorId: string, branches: { name: string, location: string }[]) => {
        if (branches.length === 0) return;
        const payload = branches.map(b => ({
            doctor_id: doctorId,
            name: b.name,
            location: b.location
        }));
        const { error } = await supabase.from('clinic_branches').insert(payload);
        if (error) throw error;
    },

    getBranches: async (doctorId: string): Promise<ClinicBranch[]> => {
        const { data, error } = await supabase.from('clinic_branches').select('*').eq('doctor_id', doctorId);
        if (error) return [];
        return data.map((b: any) => ({
            id: b.id, doctorId: b.doctor_id, name: b.name, location: b.location, createdAt: b.created_at
        }));
    },

    createBranch: async (doctorId: string, name: string, location: string) => {
        const { error } = await supabase.from('clinic_branches').insert({ doctor_id: doctorId, name, location });
        if (error) throw error;
    },

    deleteBranch: async (branchId: string) => {
        const { error } = await supabase.from('clinic_branches').delete().eq('id', branchId);
        if (error) throw error;
    },

    getClinicMembers: async (): Promise<User[]> => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return [];
        const { data } = await supabase.from('profiles')
            .select('*, clinic_branches!profiles_branch_id_fkey(name)')
            .eq('parent_id', user.id);
        return (data || []).map(mapProfileToUser);
    },

    getClinicInvites: async (): Promise<ClinicInvite[]> => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return [];
        const { data } = await supabase.from('clinic_invites').select('*').eq('doctor_id', user.id);
        return (data || []).map((i: any) => ({
            id: i.id, email: i.email, name: i.name, canSeeFinancials: i.can_see_financials, branchId: i.branch_id, status: i.status, createdAt: i.created_at
        }));
    },

    inviteClinicMember: async (name: string, email: string, canSeeFinancials: boolean, branchId?: string) => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("Not authenticated");
        const { error } = await supabase.from('clinic_invites').insert({ doctor_id: user.id, name, email, can_see_financials: canSeeFinancials, branch_id: branchId });
        if (error) throw error;
    },

    deleteClinicMember: async (id: string) => {
        const { error } = await supabase.from('profiles').update({ parent_id: null, branch_id: null }).eq('id', id);
        if (error) throw error;
    },

    deleteInvite: async (id: string) => {
        const { error } = await supabase.from('clinic_invites').delete().eq('id', id);
        if (error) throw error;
    },

    // Services
    getServices: async (targetUserId?: string): Promise<ServiceCategory[]> => {
        const { data: categories, error } = await supabase.from('service_categories').select(`
        id, 
        name, 
        services (
            id, 
            name, 
            price,
            color
        )
    `);

        if (error) throw error;

        let planId = null;

        // 1. Try to get user's assigned plan
        if (targetUserId) {
            const { data: user } = await supabase.from('profiles').select('price_plan_id').eq('id', targetUserId).single();
            if (user?.price_plan_id) {
                planId = user.price_plan_id;
            }
        }

        // 2. If no user plan, get Default Plan
        if (!planId) {
            const { data: defaultPlan } = await supabase.from('price_plans')
                .select('id')
                .eq('is_default', true)
                .limit(1)
                .maybeSingle();

            if (defaultPlan) {
                planId = defaultPlan.id;
            }
        }

        // 3. Fetch items for the determined plan
        let userPlanItems: any[] = [];
        if (planId) {
            const { data: items } = await supabase.from('price_plan_items').select('*').eq('plan_id', planId);
            userPlanItems = items || [];
        }

        return categories.map((cat: any) => ({
            id: cat.id,
            name: cat.name,
            services: cat.services.map((srv: any) => {
                const customPrice = userPlanItems.find(i => i.service_id === srv.id);
                return {
                    id: srv.id,
                    name: srv.name,
                    price: customPrice ? customPrice.price : srv.price,
                    color: srv.color
                };
            })
        }));
    },

    createCategory: async (name: string) => {
        const { error } = await supabase.from('service_categories').insert({ name });
        if (error) throw error;
    },

    deleteCategory: async (id: string) => {
        const { error } = await supabase.from('service_categories').delete().eq('id', id);
        if (error) throw error;
    },

    createService: async (categoryId: string, name: string, price: number, color?: string) => {
        const { error } = await supabase.from('services').insert({ category_id: categoryId, name, price, color });
        if (error) throw error;
    },

    deleteService: async (id: string) => {
        const { error } = await supabase.from('services').delete().eq('id', id);
        if (error) throw error;
    },

    updateService: async (id: string, updates: { name?: string, price?: number, color?: string }) => {
        const { error } = await supabase.from('services').update(updates).eq('id', id);
        if (error) throw error;
    },

    // Price Plans
    getPricePlans: async (): Promise<PricePlan[]> => {
        const { data, error } = await supabase.from('price_plans').select('*').order('created_at', { ascending: false });
        if (error) return [];
        return data.map((p: any) => ({ id: p.id, name: p.name, isDefault: p.is_default }));
    },

    createPricePlan: async (name: string, items: { serviceId: string, price: number }[]) => {
        const { data: plan, error } = await supabase.from('price_plans').insert({ name }).select().single();
        if (error) throw error;

        if (items.length > 0) {
            const payload = items.map(i => ({ plan_id: plan.id, service_id: i.serviceId, price: i.price }));
            await supabase.from('price_plan_items').insert(payload);
        }
        return plan;
    },

    getPricePlanItems: async (planId: string): Promise<PricePlanItem[]> => {
        const { data, error } = await supabase.from('price_plan_items').select('*').eq('plan_id', planId);
        if (error) return [];
        return data.map((i: any) => ({ id: i.id, planId: i.plan_id, serviceId: i.service_id, price: i.price }));
    },

    updatePricePlanItems: async (planId: string, serviceId: string, price: number) => {
        // Upsert single item
        const { error } = await supabase.from('price_plan_items').upsert({
            plan_id: planId,
            service_id: serviceId,
            price: price
        }, { onConflict: 'plan_id,service_id' });
        if (error) throw error;
    },

    setPlanAsDefault: async (planId: string) => {
        await supabase.from('price_plans').update({ is_default: false }).neq('id', '00000000-0000-0000-0000-000000000000'); // Reset all
        const { error } = await supabase.from('price_plans').update({ is_default: true }).eq('id', planId);
        if (error) throw error;
    },

    deletePricePlan: async (planId: string) => {
        const { error } = await supabase.from('price_plans').delete().eq('id', planId);
        if (error) throw error;
    },

    updateUserPricePlan: async (userId: string, planId: string) => {
        const { error } = await supabase.from('profiles').update({ price_plan_id: planId }).eq('id', userId);
        if (error) throw error;
    },

    ensureDefaultPricePlan: async (userId: string) => {
        // 1. Check if user already has a plan
        const { data: user } = await supabase.from('profiles').select('price_plan_id').eq('id', userId).single();
        if (user?.price_plan_id) return; // Already has a plan

        // 2. Get default plan
        const { data: defaultPlan } = await supabase.from('price_plans').select('id').eq('is_default', true).single();

        // 3. Assign if exists
        if (defaultPlan) {
            await supabase.from('profiles').update({ price_plan_id: defaultPlan.id }).eq('id', userId);
        }
    },

    // Designer Rates
    getDesignerRates: async (designerId: string): Promise<DesignerServiceRate[]> => {
        const { data, error } = await supabase.from('designer_service_rates').select('*').eq('designer_id', designerId);
        if (error) return [];
        return data.map((r: any) => ({ id: r.id, designerId: r.designer_id, serviceId: r.service_id, rate: r.rate }));
    },

    getAllDesignerRates: async (): Promise<DesignerServiceRate[]> => {
        const { data, error } = await supabase.from('designer_service_rates').select('*');
        if (error) return [];
        return data.map((r: any) => ({ id: r.id, designerId: r.designer_id, serviceId: r.service_id, rate: r.rate }));
    },

    upsertDesignerRate: async (designerId: string, serviceId: string, rate: number) => {
        const { error } = await supabase.from('designer_service_rates').upsert({ designer_id: designerId, service_id: serviceId, rate }, { onConflict: 'designer_id,service_id' });
        if (error) throw error;
    },

    // Milling Centers
    getMillingCenters: async (): Promise<MillingCenter[]> => {
        const { data, error } = await supabase.from('milling_centers').select('*, offerings:milling_center_offerings(service_id, cost)');
        if (error) return [];
        return data.map((c: any) => ({
            id: c.id,
            name: c.name,
            whatsappNumber: c.whatsapp_number,
            servicesList: c.services_list,
            pricingInfo: c.pricing_info,
            offerings: (c.offerings || []).map((o: any) => ({ serviceId: o.service_id, cost: o.cost }))
        }));
    },

    createMillingCenter: async (center: { name: string, whatsappNumber: string, servicesList: string, pricingInfo: string }, offerings: any[]) => {
        const { error } = await supabase.from('milling_centers').insert({
            name: center.name,
            whatsapp_number: center.whatsappNumber,
            services_list: center.servicesList,
            pricing_info: center.pricingInfo
        });
        // Offerings should be inserted separately if table exists, or handled differently
        if (error) throw error;
    },

    updateMillingOffering: async (centerId: string, serviceId: string, cost: number) => {
        const { error } = await supabase.from('milling_center_offerings').upsert({
            milling_center_id: centerId,
            service_id: serviceId,
            cost: cost
        }, { onConflict: 'milling_center_id,service_id' });
        if (error) throw error;
    },

    deleteMillingCenter: async (id: string) => {
        const { error } = await supabase.from('milling_centers').delete().eq('id', id);
        if (error) throw error;
    },

    // Settings
    getSettings: async (): Promise<SystemSettings> => {
        try {
            const { data, error } = await supabase.from('system_settings').select('*').maybeSingle();
            if (data) return {
                id: data.id,
                pmma_price: data.pmma_price,
                urgent_rate: data.urgent_rate,
                currency: data.currency,
                logoUrl: data.logo_url,
                notification_time: data.notification_time
            };
            return { id: 'default', pmma_price: 100, urgent_rate: 5, currency: 'EGP', notification_time: 20 };
        } catch (e) { return { id: 'default', pmma_price: 100, urgent_rate: 5, currency: 'EGP', notification_time: 20 }; }
    },

    updateSettings: async (settings: Partial<SystemSettings>) => {
        const payload: any = { id: 'default' };
        if (settings.pmma_price !== undefined) payload.pmma_price = settings.pmma_price;
        if (settings.urgent_rate !== undefined) payload.urgent_rate = settings.urgent_rate;
        if (settings.currency !== undefined) payload.currency = settings.currency;
        if (settings.logoUrl !== undefined) payload.logo_url = settings.logoUrl;
        if (settings.notification_time !== undefined) payload.notification_time = settings.notification_time;

        const { error } = await supabase.from('system_settings').upsert(payload);
        if (error) throw error;
    },

    // Financials & TopUps
    getTopUpRequests: async (): Promise<TopUpRequest[]> => {
        const { data, error } = await supabase.from('top_up_requests').select('*, doctor:profiles(name)').order('created_at', { ascending: false });
        if (error) return [];
        return data.map((r: any) => ({
            id: r.id,
            doctorId: r.doctor_id,
            doctorName: r.doctor?.name || 'Unknown',
            amount: r.amount,
            proofUrl: r.proof_url,
            status: r.status,
            orderId: r.order_id,
            createdAt: r.created_at
        }));
    },

    requestTopUp: async (amount: number, proofFile: File, orderId?: string) => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("Not logged in");

        const fileExt = proofFile.name.split('.').pop();
        const fileName = `receipts/${Math.random()}.${fileExt}`;
        const bucket = 'order-files';

        const { error: uploadError } = await supabase.storage.from(bucket).upload(fileName, proofFile);
        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage.from(bucket).getPublicUrl(fileName);

        const { error } = await supabase.from('top_up_requests').insert({
            doctor_id: user.id,
            amount: amount,
            proof_url: publicUrl,
            order_id: orderId
        });
        if (error) throw error;

        // NOTIFY ADMIN via WhatsApp
        try {
            await supabase.functions.invoke('send-whatsapp', {
                body: {
                    type: 'admin_payment_alert',
                    payload: {
                        doctorName: user.user_metadata.name || 'Doctor',
                        amount,
                        proofUrl: publicUrl,
                        isOrderPayment: !!orderId,
                        orderId
                    }
                }
            });
        } catch (e) { console.error("Payment notification failed", e); }
    },

    processTopUp: async (requestId: string, status: 'APPROVED' | 'REJECTED') => {
        const { error } = await supabase.rpc('process_payment_request', { request_id: requestId, new_status: status });
        if (error) throw error;
    },

    getTransactions: async (userId?: string): Promise<Transaction[]> => {
        let query = supabase.from('transactions').select('*').order('created_at', { ascending: false });
        if (userId) query = query.eq('profile_id', userId);
        const { data, error } = await query;
        if (error) return [];
        return data.map((t: any) => ({
            id: t.id,
            profileId: t.profile_id,
            amount: t.amount,
            type: t.type,
            referenceId: t.reference_id,
            description: t.description,
            createdAt: t.created_at
        }));
    },

    // Orders
    getOrders: async (): Promise<Order[]> => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return [];

        let query = supabase.from('orders').select(`
        *,
        doctor:profiles!orders_doctor_id_fkey(name),
        items:order_items(service_id, service_name, teeth, price, details),
        files:order_files(id, name, url, uploaded_at, type, size),
        clinic_branches(name)
    `).order('created_at', { ascending: false });

        const { data, error } = await query;
        if (error) throw error;
        return data.map(mapOrderFromDB);
    },

    getOrderByToken: async (token: string): Promise<Order | null> => {
        const { data: link } = await supabase.from('order_share_links').select('order_id').eq('token', token).single();
        if (!link) return null;

        const { data, error } = await supabase.from('orders').select(`
        *,
        doctor:profiles!orders_doctor_id_fkey(name),
        items:order_items(service_id, service_name, teeth, price, details),
        files:order_files(id, name, url, uploaded_at, type, size),
        clinic_branches(name)
      `).eq('id', link.order_id).single();

        if (error) return null;
        return mapOrderFromDB(data);
    },

    createOrder: async (orderData: Partial<Order>, files: File[]): Promise<Order> => {
        // 1. Sanitize Inputs (Explicit check)
        // Ensure empty strings become NULL to avoid UUID format error 22P02
        const doctorId = orderData.doctorId && orderData.doctorId.trim() !== '' ? orderData.doctorId : null;
        const branchId = orderData.branchId && orderData.branchId.trim() !== '' ? orderData.branchId : null;
        const dueDate = orderData.dueDate && orderData.dueDate.trim() !== '' ? orderData.dueDate : null;

        // 2. Insert Order
        const { data: order, error } = await supabase.from('orders').insert({
            patient_name: orderData.patientName,
            doctor_id: doctorId,
            branch_id: branchId,
            tooth_number: orderData.toothNumber,
            shade: orderData.shade,
            urgency: orderData.urgency,
            notes: orderData.notes,
            price: orderData.price,
            status: 'PENDING',
            due_date: dueDate,
            is_pmma: orderData.isPMMA,
            requires_design_check: orderData.requiresDesignCheck
        }).select().single();

        if (error) throw error;

        // 3. Insert Items
        if (orderData.items && orderData.items.length > 0) {
            const itemsPayload = orderData.items.map(i => ({
                order_id: order.id,
                service_id: i.serviceId,
                service_name: i.serviceName,
                teeth: i.teeth,
                price: i.price,
                details: i.details
            }));
            await supabase.from('order_items').insert(itemsPayload);
        }

        // 4. Log Timeline
        await api.logTimelineEvent(order.id, 'Order Created', 'Order received in system', 'PENDING');

        // 5. Handle Notifications (UltraMsg)
        try {
            // A. Generate Share Link (using new helper logic implicitly via manual create here for speed)
            const token = await api.createShareLink(order.id);

            // B. Get Doctor Details
            const { data: doctor } = await supabase.from('profiles').select('name, phone').eq('id', order.doctor_id).single();

            // C. Prepare Data
            const itemsSummary = orderData.items?.map(i => `- ${i.serviceName} (${i.teeth.join(',')})`).join('\n') || 'Custom Request';
            const notificationData = {
                patientName: order.patient_name,
                doctorName: doctor?.name || 'Unknown Doctor',
                doctorPhone: doctor?.phone || '',
                dueDate: order.due_date || new Date().toISOString(),
                shade: order.shade,
                requiredDetails: `${itemsSummary}\n\nNotes: ${order.notes || 'None'}`,
                linkToken: token
            };

            // D. Send to Admin
            await supabase.functions.invoke('send-whatsapp', {
                body: { type: 'admin_notification', payload: notificationData }
            });

            // E. Send to Doctor (Confirmation)
            if (doctor?.phone) {
                await supabase.functions.invoke('send-whatsapp', {
                    body: { type: 'doctor_creation_notification', payload: notificationData }
                });
            }

        } catch (notifyError) {
            console.error("Notification failed:", notifyError);
        }

        return mapOrderFromDB(order);
    },

    triggerDeadlineCheck: async (force = false) => {
        try {
            const { data, error } = await supabase.functions.invoke('send-whatsapp', {
                body: { type: 'daily_deadline_check', force }
            });
            if (error) throw error;
            return data;
        } catch (e) {
            console.error("Deadline check failed", e);
            throw e;
        }
    },

    updateOrderDetails: async (orderId: string, updates: any) => {
        const { error } = await supabase.from('orders').update({
            patient_name: updates.patientName,
            shade: updates.shade,
            urgency: updates.urgency,
            notes: updates.notes,
            due_date: updates.dueDate || null,
            price: updates.price,
            is_pmma: updates.isPMMA,
            requires_design_check: updates.requiresDesignCheck
        }).eq('id', orderId);

        if (error) throw error;

        if (updates.items) {
            await supabase.from('order_items').delete().eq('order_id', orderId);
            const itemsPayload = updates.items.map((i: any) => ({
                order_id: orderId,
                service_id: i.serviceId,
                service_name: i.serviceName,
                teeth: i.teeth,
                price: i.price,
                details: i.details
            }));
            await supabase.from('order_items').insert(itemsPayload);
        }
    },

    updateOrderStatus: async (orderId: string, status: OrderStatus, notes?: string) => {
        const { error } = await supabase.from('orders').update({ status }).eq('id', orderId);
        if (error) throw error;
        await api.logTimelineEvent(orderId, `Status: ${status.replace('_', ' ')}`, notes || '', status);
    },

    bulkUpdateStatus: async (orderIds: string[], status: OrderStatus) => {
        const { error } = await supabase.from('orders').update({ status }).in('id', orderIds);
        if (error) throw error;
        for (const id of orderIds) {
            await api.logTimelineEvent(id, `Status Updated`, `Bulk update to ${status}`, status);
        }
    },

    assignTechnician: async (orderId: string, technicianId: string) => {
        const { error } = await supabase.from('orders').update({ technician_id: technicianId, status: 'DESIGNING' }).eq('id', orderId);
        if (error) throw error;
        await api.logTimelineEvent(orderId, 'Technician Assigned', 'Case assigned to designer', 'DESIGNING');

        // Notify Technician
        try {
            const data = await getNotificationData(orderId);
            if (data && data.technician?.phone) {
                const caseType = data.order.items?.map((i: any) => i.service_name).join(', ') || 'Custom Case';
                await supabase.functions.invoke('send-whatsapp', {
                    body: {
                        type: 'technician_notification',
                        payload: {
                            patientName: data.order.patient_name,
                            caseType: caseType,
                            technicianName: data.technician.name,
                            phone: data.technician.phone,
                            linkToken: data.token
                        }
                    }
                });
            }
        } catch (e) { console.error("Tech notify failed", e); }
    },

    deleteOrder: async (orderId: string) => {
        const { error } = await supabase.from('orders').delete().eq('id', orderId);
        if (error) throw error;
    },

    bulkDeleteOrders: async (orderIds: string[]) => {
        // First, delete related top_up_requests to avoid foreign key constraint violations
        const { error: topUpError } = await supabase
            .from('top_up_requests')
            .delete()
            .in('order_id', orderIds);

        if (topUpError) throw topUpError;

        // Now delete the orders
        const { error } = await supabase.from('orders').delete().in('id', orderIds);
        if (error) throw error;
    },

    duplicateOrder: async (order: Order, userId: string) => {
        const { data: newOrder, error } = await supabase.from('orders').insert({
            patient_name: order.patientName + ' (Copy)',
            doctor_id: userId,
            tooth_number: order.toothNumber,
            shade: order.shade,
            urgency: order.urgency,
            notes: order.notes,
            price: order.price,
            status: 'PENDING',
            is_pmma: order.isPMMA,
            requires_design_check: order.requiresDesignCheck
        }).select().single();

        if (error) throw error;

        if (order.items) {
            const itemsPayload = order.items.map(i => ({
                order_id: newOrder.id,
                service_id: i.serviceId,
                service_name: i.serviceName,
                teeth: i.teeth,
                price: i.price,
                details: i.details
            }));
            await supabase.from('order_items').insert(itemsPayload);
        }
    },

    setOrderMillingCenter: async (orderId: string, centerId: string) => {
        const { error } = await supabase.from('orders').update({ milling_center_id: centerId }).eq('id', orderId);
        if (error) throw error;
    },

    // Files
    uploadFile: async (orderId: string | null, file: File): Promise<string> => {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Math.random().toString(36).substring(2)}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
        const bucket = 'order-files';

        const { error: uploadError } = await supabase.storage.from(bucket).upload(fileName, file);
        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage.from(bucket).getPublicUrl(fileName);

        if (orderId) {
            await supabase.from('order_files').insert({
                order_id: orderId,
                name: file.name,
                url: publicUrl,
                type: file.type,
                size: file.size
            });
        }
        return publicUrl;
    },

    submitDesign: async (orderId: string, htmlUrl: string, zipUrl: string, requiresCheck: boolean) => {
        const status = requiresCheck ? 'NEEDS_REVIEW' : 'MILLING';
        const { error } = await supabase.from('orders').update({
            design_files: { htmlPreview: htmlUrl, productionZip: zipUrl },
            status: status
        }).eq('id', orderId);
        if (error) throw error;

        await api.logTimelineEvent(orderId, 'Design Submitted', 'Files uploaded by technician', status);

        // Notify Admin & Tech (confirm)
        try {
            const data = await getNotificationData(orderId);
            if (data) {
                await supabase.functions.invoke('send-whatsapp', {
                    body: {
                        type: 'design_submission',
                        payload: {
                            linkToken: data.token,
                            patientName: data.order.patient_name,
                            doctorName: data.doctor?.name || 'Unknown',
                            techName: data.technician?.name || 'Designer',
                            techPhone: data.technician?.phone
                        }
                    }
                });
            }
        } catch (e) { console.error("Submit notify failed", e); }
    },

    notifyFileUpload: async (orderId: string, uploaderName: string, count: number, phone?: string) => {
        try {
            const data = await getNotificationData(orderId);
            if (data) {
                await supabase.functions.invoke('send-whatsapp', {
                    body: {
                        type: 'file_upload',
                        payload: {
                            linkToken: data.token,
                            patientName: data.order.patient_name,
                            doctorName: data.doctor?.name || 'Unknown',
                            uploaderName,
                            fileCount: count,
                            uploaderPhone: phone
                        }
                    }
                });
            }
        } catch (e) { console.error("Upload notify failed", e); }
    },

    createShareLink: async (orderId: string): Promise<string> => {
        // Check for existing token first
        const { data: existing } = await supabase.from('order_share_links').select('token').eq('order_id', orderId).limit(1).maybeSingle();
        if (existing) return existing.token;

        const token = Math.random().toString(36).substring(2) + Math.random().toString(36).substring(2);
        const { error } = await supabase.from('order_share_links').insert({ order_id: orderId, token });
        if (error) throw error;
        return token;
    },

    // Timeline & Chat
    getTimeline: async (orderId: string): Promise<TimelineEvent[]> => {
        const { data, error } = await supabase.from('timeline_events').select('*').eq('order_id', orderId).order('created_at', { ascending: false });
        if (error) return [];
        return data.map((e: any) => ({
            id: e.id, orderId: e.order_id, title: e.title, description: e.description, status: e.status, createdAt: e.created_at
        }));
    },

    logTimelineEvent: async (orderId: string, title: string, description: string, status?: string) => {
        const { error } = await supabase.from('timeline_events').insert({ order_id: orderId, title, description, status });
        if (error) console.error("Timeline log error", error);
    },

    getMessages: async (orderId: string): Promise<Message[]> => {
        const { data, error } = await supabase.from('messages').select(`*, sender:profiles(name)`).eq('order_id', orderId).order('created_at', { ascending: true });
        if (error) return [];
        return data.map((m: any) => ({
            id: m.id, orderId: m.order_id, senderId: m.sender_id, senderName: m.sender?.name || 'Unknown', content: m.content, createdAt: m.created_at
        }));
    },

    sendMessage: async (orderId: string, senderId: string, content: string) => {
        const { error } = await supabase.from('messages').insert({ order_id: orderId, sender_id: senderId, content });
        if (error) throw error;
    },

    moveToMilling: async (order: Order) => {
        // First, deduct the order total from the doctor's balance
        try {
            // Get the current user balance
            const { data: userData, error: userError } = await supabase
                .from('users')
                .select('balance')
                .eq('id', order.doctorId)
                .single();

            if (userError) throw userError;

            // Check if doctor has sufficient balance
            if (userData.balance < order.price) {
                throw new Error('Insufficient balance');
            }

            // Deduct the balance
            const { error: balanceError } = await supabase
                .from('users')
                .update({ balance: userData.balance - order.price })
                .eq('id', order.doctorId);

            if (balanceError) throw balanceError;

            // Mark order as paid (but keep current status)
            const { error: statusError } = await supabase
                .from('orders')
                .update({
                    paid: true,
                    updatedAt: new Date().toISOString()
                })
                .eq('id', order.id);

            if (statusError) throw statusError;

        } catch (error: any) {
            throw new Error(error.message || 'Payment failed');
        }
    },

    requestContact: async (token: string) => {
        // Use RPC if available, or manual logic
        const { data, error } = await supabase.rpc('process_contact_request', { link_token: token });
        if (error) throw error;
        if (data && data.error) throw new Error(data.error);

        // Trigger notification via Edge Function
        if (data && data.success) {
            await supabase.functions.invoke('send-whatsapp', {
                body: {
                    type: 'contact_request_trigger',
                    payload: {
                        doctorName: data.doctor_name,
                        doctorPhone: data.doctor_phone,
                        patientName: data.patient_name
                    }
                }
            });
        }
    },

    // Notifications
    getNotifications: async (): Promise<AppNotification[]> => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return [];
        const { data } = await supabase.from('notifications').select('*').eq('user_id', user.id).order('created_at', { ascending: false }).limit(20);
        return (data || []).map((n: any) => ({
            id: n.id, userId: n.user_id, title: n.title, message: n.message, isRead: n.is_read, link: n.link, createdAt: n.created_at, type: n.type
        }));
    },

    markNotificationRead: async (id: string) => {
        await supabase.from('notifications').update({ is_read: true }).eq('id', id);
    },

    markAllNotificationsRead: async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) await supabase.from('notifications').update({ is_read: true }).eq('user_id', user.id);
    }
};
